import '@/utils/kitchenBootstrap.js';
import { createApp } from 'vue';
import { 
  IonicVue, 
  IonPage, 
  IonContent, 
  IonGrid, 
  IonRow, 
  IonCol, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonButtons, 
  IonButton, 
  IonIcon, 
  IonLabel, 
  IonItem, 
  IonList,
  IonInput,
  IonCheckbox,
  IonTabs,
  IonTabBar,
  IonTabButton,
} from '@ionic/vue';
import App from './App.vue';
import router from './router';
import store from './store';

// Firebase v9 compat – allows existing firebase.xyz() calls to keep working
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import firebaseCredentials from '@/constants/firebase.js';

import registerServiceWorker from '@/utils/registerServiceWorker.js';
import { installStaleChunkReloadHandlers } from '@/utils/staleChunkReload.js';
import moment from 'moment';
import 'moment/locale/de';

moment.locale('de');

/* Core Ionic CSS */
import '@ionic/vue/css/core.css';
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';
import '@/styles/snackbar.scss';
import '@/theme/variables.css';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import '@/styles/tutorial.scss';
import '@/styles/ios-input-zoom-fix.scss';
import { installIosOrientationZoomFix } from '@/utils/iosOrientationZoomFix.js';
import { initDefaultAuthPersistence } from '@/utils/partnerAuthPersistence.js';
import { getKitchenBindToken } from '@/utils/kitchenBinding';
import { isStandalonePwa } from '@/utils/pwaStandalone';

installIosOrientationZoomFix();

// Bugfix getDeviceInfo
if (navigator.getBattery) { navigator.getBattery = () => Promise.reject(); }

// Firebase init
firebase.initializeApp(firebaseCredentials);
firebase.auth().languageCode = 'de';
void initDefaultAuthPersistence();

// iOS copies Safari login cookies into a new Home Screen app — never run partner auth in Küchen-PWA.
const kitchenBindAtLaunch = getKitchenBindToken()
  || (isStandalonePwa() && document.cookie.includes('ollaa_kitchen_bind='));
if (kitchenBindAtLaunch && (isStandalonePwa() || window.location.pathname.startsWith('/kueche'))) {
  void firebase.auth().signOut();
}

// Create the Vue 3 app
const app = createApp(App)
  .use(IonicVue)
  .use(store)
  .use(router)
  .use(FloatingVue, {
    themes: {
      'ollaa-tutorial': {
        $extend: 'tooltip',
        handleResize: false,
      },
    },
  });

// Globales Sicherheitsnetz für veraltete Vue 2 Templates
app.component('ion-page', IonPage);
app.component('ion-content', IonContent);
app.component('ion-grid', IonGrid);
app.component('ion-row', IonRow);
app.component('ion-col', IonCol);
app.component('ion-header', IonHeader);
app.component('ion-toolbar', IonToolbar);
app.component('ion-title', IonTitle);
app.component('ion-buttons', IonButtons);
app.component('ion-button', IonButton);
app.component('ion-icon', IonIcon);
app.component('ion-label', IonLabel);
app.component('ion-item', IonItem);
app.component('ion-list', IonList);
app.component('ion-input', IonInput);
app.component('ion-checkbox', IonCheckbox);
app.component('ion-tabs', IonTabs);
app.component('ion-tab-bar', IonTabBar);
app.component('ion-tab-button', IonTabButton);

// Global $ready helper (migrated from Vue.mixin)
app.config.globalProperties.$ready = function $ready(fn) {
  if (import.meta.env.PROD) {
    return this.$nextTick(fn);
  }
  return setTimeout(() => {
    this.$nextTick(fn);
  }, 500);
};

// Mount after router is ready (Ionic standard)
installStaleChunkReloadHandlers();

router.isReady().then(() => {
  app.mount('#app');
  registerServiceWorker();
});
